import { useEffect, useState, type ComponentType } from 'react';
import {
  Building2,
  LogOut,
  Monitor,
  Moon,
  Settings as SettingsIcon,
  Smartphone,
  Sun,
  Users,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { getToken, clearToken, api } from './lib/api';
import { useI18n } from './lib/i18n';
import { useTheme } from './lib/theme';
import { Logo } from './components/Logo';
import { LoginPage } from './pages/Login';
import { OperationsPage, type OperationsSub } from './pages/Operations';
import { MembersPage, type MembersSub } from './pages/Members';
import { DeviceGroupPage, type DeviceGroupSub } from './pages/DeviceGroup';
import { TenantsPage } from './pages/platform/Tenants';
import { TelegramSettingsPage } from './pages/platform/TelegramSettings';

type Group = 'workspaces' | 'members' | 'devices' | 'monitoring' | 'settings';
type SubKey = OperationsSub | MembersSub | DeviceGroupSub | 'tenants' | 'telegram';

type IconCmp = ComponentType<{ className?: string }>;

interface NavGroup {
  id: Group;
  labelKey: string;
  icon: IconCmp;
  defaultSub: SubKey;
  // 비어있으면 sub-menu 안 그림 (1차 메뉴 단독)
  subs: { id: SubKey; labelKey: string }[];
}

// 운영 시나리오 순서: 회사 등록 → 사용자 가입 → 앱 설치 → 모니터링
const MAIN_NAV: NavGroup[] = [
  {
    id: 'workspaces',
    labelKey: 'nav.workspaces',
    icon: Building2,
    defaultSub: 'tenants',
    subs: [], // 단일 페이지 — sub 없음
  },
  {
    id: 'members',
    labelKey: 'nav.members',
    icon: Users,
    defaultSub: 'users',
    subs: [
      { id: 'users', labelKey: 'tabs.users' },
      { id: 'access', labelKey: 'tabs.access' },
    ],
  },
  {
    id: 'devices',
    labelKey: 'nav.devicesGroup',
    icon: Smartphone,
    defaultSub: 'pairing',
    subs: [
      { id: 'pairing', labelKey: 'tabs.pairing' },
      { id: 'apikeys', labelKey: 'tabs.apikeys' },
    ],
  },
  {
    id: 'monitoring',
    labelKey: 'nav.monitoring',
    icon: Monitor,
    defaultSub: 'metrics',
    // 'agents' sub 가림 — 에이전트 API key prefix 노출 차단 (docs/hidden-coming-soon.md #7 참고)
    subs: [
      { id: 'metrics', labelKey: 'tabs.metrics' },
      { id: 'activity', labelKey: 'tabs.activity' },
    ],
  },
  {
    id: 'settings',
    labelKey: 'nav.settings',
    icon: SettingsIcon,
    defaultSub: 'telegram',
    subs: [
      { id: 'telegram', labelKey: 'tabs.telegram' },
    ],
  },
];

function GroupHeader({ group, active, onClick }: { group: NavGroup; active: boolean; onClick: () => void }) {
  const { t } = useI18n();
  const Icon = group.icon;
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'group relative flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm font-medium transition-colors',
        active
          ? 'text-sidebar-accent-foreground'
          : 'text-sidebar-foreground/85 hover:bg-sidebar-accent/40 hover:text-sidebar-accent-foreground',
      )}
    >
      <Icon
        className={cn(
          'size-4 shrink-0 transition-colors',
          active ? 'text-primary' : 'text-sidebar-foreground/55 group-hover:text-sidebar-foreground/80',
        )}
      />
      <span className="truncate">{t(group.labelKey)}</span>
    </button>
  );
}

function SubNavButton({ labelKey, active, onClick }: { labelKey: string; active: boolean; onClick: () => void }) {
  const { t } = useI18n();
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'relative flex w-full items-center rounded-lg pl-9 pr-3 py-1.5 text-left text-[13px] transition-colors',
        active
          ? 'bg-sidebar-accent text-sidebar-accent-foreground'
          : 'text-sidebar-foreground/65 hover:bg-sidebar-accent/50 hover:text-sidebar-accent-foreground',
      )}
    >
      {active && (
        <span className="absolute left-3 top-1/2 h-4 -translate-y-1/2 rounded-r-full bg-primary" style={{ width: '2.5px' }} />
      )}
      <span className="truncate">{t(labelKey)}</span>
    </button>
  );
}

function ThemeButton() {
  const { t } = useI18n();
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === 'dark';
  return (
    <button
      type="button"
      onClick={toggleTheme}
      aria-label={t(isDark ? 'theme.light' : 'theme.dark')}
      title={t(isDark ? 'theme.light' : 'theme.dark')}
      className="flex size-9 items-center justify-center rounded-lg border border-sidebar-border/60 text-sidebar-foreground/65 transition-colors hover:bg-sidebar-accent/50 hover:text-sidebar-accent-foreground"
    >
      {isDark ? <Sun className="size-4" /> : <Moon className="size-4" />}
    </button>
  );
}

export function App() {
  const { t } = useI18n();
  const [authed, setAuthed] = useState(!!getToken());
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);
  const [group, setGroup] = useState<Group>('workspaces');
  const [sub, setSub] = useState<SubKey>('tenants');

  // 일반 운영자(workspace owner)는 workspaces만, platform admin은 전체 그룹.
  const visibleNav = isPlatformAdmin ? MAIN_NAV : MAIN_NAV.filter((g) => g.id === 'workspaces');

  const selectGroup = (g: Group) => {
    setGroup(g);
    const meta = MAIN_NAV.find((n) => n.id === g);
    if (meta) setSub(meta.defaultSub);
  };

  useEffect(() => {
    const onStorage = () => setAuthed(!!getToken());
    const onAuthCleared = () => setAuthed(false);
    window.addEventListener('storage', onStorage);
    window.addEventListener('swk-admin-auth-cleared', onAuthCleared);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('swk-admin-auth-cleared', onAuthCleared);
    };
  }, []);

  useEffect(() => {
    if (!authed) { setIsPlatformAdmin(false); return; }
    let cancelled = false;
    api<{ isPlatformAdmin?: boolean }>('/v1/auth/me')
      .then((res) => { if (!cancelled) setIsPlatformAdmin(!!res?.isPlatformAdmin); })
      .catch(() => { if (!cancelled) setIsPlatformAdmin(false); });
    return () => { cancelled = true; };
  }, [authed]);

  useEffect(() => {
    const allowed = isPlatformAdmin ? MAIN_NAV : MAIN_NAV.filter((g) => g.id === 'workspaces');
    if (!allowed.find((g) => g.id === group)) {
      setGroup('workspaces');
      setSub('tenants');
    }
  }, [isPlatformAdmin, group]);

  if (!authed) {
    return <LoginPage onLogin={() => setAuthed(true)} />;
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="flex min-h-screen">
        <aside className="sticky top-0 hidden h-screen w-64 shrink-0 flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground lg:flex">
          <div className="border-b border-sidebar-border px-5 py-5">
            <Logo className="h-6 w-auto text-sidebar-foreground" />
            <div className="mt-2 text-[10px] uppercase tracking-[0.14em] text-sidebar-foreground/45">{t('nav.console')}</div>
          </div>

          <nav className="flex-1 overflow-y-auto px-3 py-4">
            <div className="space-y-1">
              {visibleNav.map((g) => (
                <div key={g.id} className="space-y-0.5">
                  <GroupHeader group={g} active={group === g.id} onClick={() => selectGroup(g.id)} />
                  <div className="space-y-0.5">
                    {g.subs.map((s) => (
                      <SubNavButton
                        key={s.id}
                        labelKey={s.labelKey}
                        active={group === g.id && sub === s.id}
                        onClick={() => { setGroup(g.id); setSub(s.id); }}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </nav>

          <div className="flex items-center gap-2 border-t border-sidebar-border p-3">
            <button
              type="button"
              onClick={() => { clearToken(); setAuthed(false); }}
              className="flex flex-1 items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-sidebar-foreground/70 transition-colors hover:bg-sidebar-accent/50 hover:text-sidebar-accent-foreground"
            >
              <LogOut className="size-4 shrink-0 text-sidebar-foreground/55" />
              <span>{t('nav.logout')}</span>
            </button>
            <ThemeButton />
          </div>
        </aside>

        <main className="min-w-0 flex-1 overflow-auto">
          <div className="mx-auto w-full max-w-[1680px] px-6 py-8 lg:px-10">
            {group === 'workspaces' && <TenantsPage />}
            {group === 'members' && <MembersPage sub={sub as MembersSub} />}
            {group === 'devices' && <DeviceGroupPage sub={sub as DeviceGroupSub} />}
            {group === 'monitoring' && <OperationsPage sub={sub as OperationsSub} />}
            {group === 'settings' && sub === 'telegram' && <TelegramSettingsPage />}
          </div>
        </main>
      </div>
    </div>
  );
}
